import React, { useEffect, useState } from "react";
import { HeadFC, PageProps, graphql, navigate } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { Translate } from "@comps/shared/Translate";
import { Seo } from "@comps/shared/Seo";
import { Layout } from "@/layout/Layout";

import { usePopupContext } from "@/contexts/PopUp";
import { usePageContext } from "@/contexts/Page";
import { useLocations } from "@/hooks/Location";

const ReservationsPage: React.FC<PageProps<any>> = ({ data }) => {
  const [widgetInfo, setWidgetInfo] = useState<any>(null);
  const { reservations, geoLocations } = useLocations();
  const { updateContext } = usePageContext();
  const { showPopup } = usePopupContext();

  const initializeWidget = (info: any) => {
    if (!info.widgetSupplier) {
      console.error("No widget supplier assigned");
      return;
    }

    if (info.widgetSupplier[0] === "redirection") {
      console.log("User will be redirected outside the website...");
      window.open(info.widgetScriptUrl, "_blank");
      return;
    }

    showPopup(<div className="reservations" id="widget-container" />);
    setWidgetInfo(info);
  };

  useEffect(() => {
    const container = document.getElementById("widget-container");
    if (!container) return;
    while (container.firstChild) {
      console.log("Cleaning up the widget container...");
      container.removeChild(container.firstChild);
    }

    if (widgetInfo) {
      switch (widgetInfo.widgetSupplier[0]) {
        case "iframe":
          console.log("Loading iframe widget...");

          const iframe = document.createElement("iframe");
          iframe.setAttribute("width", "384");
          iframe.setAttribute("height", "384");
          iframe.setAttribute("src", widgetInfo.widgetScriptUrl);
          iframe.setAttribute("frameborder", "0");
          container.appendChild(iframe);
          break;
        case "opentable":
          console.log("Loading OpenTable widget...");

          const scriptSrc = widgetInfo.widgetScriptUrl;
          const existingScript = container.querySelector(
            `script[src="${scriptSrc}"]`
          );

          if (existingScript) {
            console.log("Removing existing script...");
            container.removeChild(existingScript);
          }

          const script = document.createElement("script");
          script.src = scriptSrc;
          script.async = true;
          container.appendChild(script);
          break;
        default:
          console.error(
            "Unknown widget supplier: ",
            widgetInfo.widgetSupplier[0]
          );
      }
    }
  }, [widgetInfo]);

  // React.useEffect(() => {
  //   updateContext("", "Reservations en", "Info", "page");
  // }, []);

  return (
    <Layout footer={false} headerTransition={false}>
      <main id="reservations-page">
        <GatsbyImage
          image={data.wpPage.featuredImage.node.gatsbyImage}
          alt={data.wpPage.featuredImage.node.altText}
          className="decoration"
        />
        <div className="inner">
          <div className="inner__presentation">
            <button
              className="presentation__redirection"
              onClick={() => navigate(-1)}
            />
            <h1 className="presentation__title">Make a reservation</h1>
          </div>
          <div className="inner__content">
            {geoLocations
              .slice()
              .reverse()
              .map((geolocation: GeoLocation, index: number) => {
                let filteredElements = reservations.filter(
                  (location: Location) =>
                    location.geoLocations.nodes.some(
                      (geoLocation: GeoLocation) =>
                        geoLocation.slug === geolocation.slug
                    )
                );

                if (geolocation.slug === "south-america") {
                  filteredElements = filteredElements.sort((a, b) => {
                    if (a.slug === "peru") return -1;
                    if (b.slug === "peru") return 1;
                    return 0;
                  });
                }

                if (filteredElements.length === 0) {
                  return null;
                }

                return (
                  <div className="content__group" key={index}>
                    <h3 className="group__title">
                      <Translate id={geolocation.slug.replace("-", "_")} />
                    </h3>
                    <ul className="group__elements">
                      {filteredElements.map(
                        (location: Location, index: number) => (
                          <li className="element" key={index}>
                            <button
                              className="element__redirection"
                              onClick={() => {
                                initializeWidget(location.locationReservation);
                              }}
                            >
                              {location.title}
                            </button>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                );
              })}
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default ReservationsPage;

export const Head: HeadFC<{ wpPage: any }> = ({ data }) => {
  const { wpPage } = data;
  return (
    <Seo
      seoDescription={wpPage.seo.opengraphDescription}
      SeoImage={wpPage.seo.opengraphImage?.publicUrl}
      metaDescription={wpPage.seo.metaDesc}
      seoTitle={wpPage.seo.opengraphTitle}
      seoType={wpPage.seo.opengraphType}
      SeoUrl={wpPage.seo.opengraphUrl}
      metatitle={wpPage.seo.title}
    />
  );
};

export const pageQuery = graphql`
  query ReservationsPageQuery {
    wpPage(slug: { eq: "reservations" }) {
      id
      featuredImage {
        node {
          title
          altText
          gatsbyImage(
            formats: WEBP
            breakpoints: 3
            quality: 100
            height: 2112
            width: 1035
            fit: COVER
          )
        }
      }
      seo {
        title
        metaDesc
        opengraphUrl
        opengraphType
        opengraphTitle
        opengraphDescription
        opengraphImage {
          publicUrl
        }
      }
    }
  }
`;
